/**
 * API Client
 * Fetch wrapper that attaches Authorization header from Amplify session
 */

let apiBaseUrl = '';

try {
  const { amplifyConfig } = require('../config/amplify.generated');
  apiBaseUrl = amplifyConfig.API?.endpoint || '';
} catch (e) {
  // Config not yet generated
}

/**
 * Set the API base URL (called after config is loaded)
 */
export function setApiBaseUrl(url) {
  apiBaseUrl = url;
}

/**
 * Make an authenticated API request
 */
function toCamelCase(obj) {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj === null || typeof obj !== 'object') return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    result[camelKey] = toCamelCase(value);
  }
  return result;
}

export async function apiRequest(path, options = {}) {
  const { fetchAuthSession } = await import('aws-amplify/auth');

  let token = null;
  try {
    const session = await fetchAuthSession();
    token = session.tokens?.idToken?.toString();
  } catch (e) {
    // Not authenticated
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error?.message || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }

  // API wraps responses in { success, data, ... } — unwrap and convert to camelCase
  if (data.success && data.data !== undefined) {
    return toCamelCase(data.data);
  }

  return toCamelCase(data);
}

/**
 * Convenience methods
 */
export const api = {
  get: (path) => apiRequest(path, { method: 'GET' }),
  post: (path, body) => apiRequest(path, { method: 'POST', body: JSON.stringify(body) }),
  put: (path, body) => apiRequest(path, { method: 'PUT', body: JSON.stringify(body) }),
};
